import { Cube01, Trash01, Plus, Copy03, ChevronUp, ChevronDown, InfoCircle, Edit01 } from '@untitled-ui/icons-react/build/cjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { DIMENSION_REQUIRED_EQUIPMENT_TYPES, MEASUREMENT, QUOTE_CLASSIFICATION } from 'src/constants/common';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { addressDetailsData, isViewQuoteData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import {
    calculateLinearFootage,
    findServiceTypeIdBySlug,
    formatLinearFootage,
    getCalculatedFreightClass,
    getLabelByValue,
    getStackingBlockedMessage,
    isStackingBlocked,
} from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { buildStopOptions, clearInvalidStopRouting, IEditStopFormData } from '../AddQuote/multiStop.helpers';
import { initialQuoteDetailAction, MAX_DIMENSION_LIMIT } from '../quoteDetail.const';

import AdditionalServiceModal from './AdditionalServiceModal';
import DangerousGoodsModal from './DangerousGoodsModal';
import InBondModal from './InBondModal';
import { IDangerousGoodsForm, IInBondForm } from './quoteDetailModal.interface';
import VisualizeUnit from './VisualizeUnit';

const initDimensionData = {
    id: null,
    handlingUnit: 1,
    handlingUnitNo: 0,
    weight: 0,
    itemLength: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    isStack: true,
    commodity: null,
    freightClass: 0,
    itemClassForDayross: 0,
};

interface IProps {
    // Task 40 (client decision) — the middle-stop activity hints edited on each Stop N tab
    // (EditStopDetails.tsx), threaded here so Load Details' routing dropdowns hard-filter by hint
    // exactly like Add Quote's Dimensions step does (buildStopOptions), and so a hint change
    // clears any now-invalid routing (clearInvalidStopRouting) — same stale-routing safety net.
    // Optional/defaults to [] so this component's other caller (QuoteDetailModal.tsx, the legacy
    // modal) is unaffected.
    stopsFormData?: IEditStopFormData[];
}

const DimensionsTab = ({ stopsFormData = [] }: IProps) => {
    const { t } = useTranslation();
    const isViewQuote = useSelector(isViewQuoteData);
    const { commodityOptions } = useSelector((data: RootState) => data.basic);
    const { handlingUnitOptions, serviceTypes = { PICKUP: [], DELIVERY: [], OTHER: [] }, isVehicleTypeLoading } = useAddressDetails();
    const [action, setAction] = useState(initialQuoteDetailAction);

    const handleAction =
        (type: string, isOpen: boolean = true) =>
        () =>
            setAction((old) => ({ ...old, [type]: isOpen }));
    const handleClose = () => setAction(initialQuoteDetailAction);

    const { control, watch, setValue, trigger, formState, getValues } = useFormContext();
    const { isSubmitted } = formState;
    const [currentData, setCurrentData] = useState<{
        index: number;
        data: any;
    } | null>(null);
    const isInitialMount = useRef(true);

    const {
        fields: dimensionsOptionFields,
        append: appendOption,
        remove: removeOption,
    } = useFieldArray({
        control,
        name: 'dimensions',
        keyName: 'uuid',
    });

    const selectedServices = watch('serviceId');
    const weightMeasurement = watch('weightMeasurement');
    const lengthMeasurement = watch('lengthMeasurement');
    const lengthMeasurementLabel = getLabelByValue(watch('lengthMeasurement'));
    const dimensions = watch('dimensions');
    const primaryEquipmentType = watch('primaryEquipmentType');
    const primaryEquipmentTypeFullName = watch('primaryEquipmentTypeFullName');
    const classification = watch('classification');
    const [openItems, setOpenItems] = useState(new Set([0]));

    const isDimensionRequired = useMemo(() => {
        const fullName = typeof primaryEquipmentTypeFullName === 'string' ? primaryEquipmentTypeFullName : Array.isArray(primaryEquipmentTypeFullName) ? primaryEquipmentTypeFullName[0] : 'dry van';

        return DIMENSION_REQUIRED_EQUIPMENT_TYPES.some((type) => fullName?.toLowerCase().includes(type));
    }, [primaryEquipmentTypeFullName]);

    // Task 37 (client decision) — per-item pick/drop stop routing, matching the Add Quote wizard's
    // Dimensions step (same buildStopOptions/SelectBox pattern — see LineItem.tsx). Classic
    // (non-multi-stop) quotes have no quote_stops rows at all, so stopCount defaults to 2 and the
    // routing selectors never render (identical to the create flow's own stopCount > 2 gate).
    const addressDetails = useSelector(addressDetailsData);
    const stops: any[] = addressDetails?.stops || [];
    const stopCount = stops.length > 0 ? stops.length : 2;
    const stopIdToPosition = useMemo(() => new Map(stops.map((s: any) => [s.id, s.position])), [stops]);
    const hintByPosition = useMemo(() => new Map(stopsFormData.map((s) => [s.position, s.activityHint])), [stopsFormData]);
    const stopsRecord = useMemo(() => {
        const record: Record<number, any> = {};

        stops.forEach((s: any) => {
            record[s.position] = { stopAddress: { city: s.city, state: s.state, stateCode: s.stateCode }, companyName: s.companyName, activityHint: hintByPosition.get(s.position) };
        });

        return record;
    }, [stops, hintByPosition]);
    const { pickupOptions, dropOptions } = useMemo(
        () =>
            buildStopOptions(t, {
                stopCount,
                shipperCity: addressDetails?.shipperCity,
                shipperState: addressDetails?.shipperState,
                shipperStateCode: addressDetails?.shipperStateCode,
                shipperCompanyName: addressDetails?.shipperCompanyName,
                consigneeCity: addressDetails?.consigneeCity,
                consigneeState: addressDetails?.consigneeState,
                consigneeStateCode: addressDetails?.consigneeStateCode,
                consigneeCompanyName: addressDetails?.consigneeCompanyName,
                stops: stopsRecord,
            }),
        [
            t,
            stopCount,
            addressDetails?.shipperCity,
            addressDetails?.shipperState,
            addressDetails?.shipperStateCode,
            addressDetails?.shipperCompanyName,
            addressDetails?.consigneeCity,
            addressDetails?.consigneeState,
            addressDetails?.consigneeStateCode,
            addressDetails?.consigneeCompanyName,
            stopsRecord,
        ]
    );

    // Task 40 (client decision) — mirrors Add Quote's Dimensions/index.tsx exactly: an item routed
    // through a stop BEFORE its hint changed to something incompatible (e.g. "Delivers to: Stop 2"
    // when Stop 2 gets re-hinted Pick-up-only) is auto-cleared back to unset rather than left
    // pointing at an option that just silently disappeared from its dropdown.
    useEffect(() => {
        if (stopCount <= 2) return;

        const toClear = clearInvalidStopRouting({ stops: stopsRecord, items: watch('dimensions') || [] });

        toClear.forEach(({ index, field }) => {
            setValue(`dimensions.${index}.${field}`, null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopsRecord, stopCount]);

    // Backfills pickupStopPosition/dropStopPosition (UI-facing) from the raw pickupStopId/dropStopId
    // the BE returns, once per data-load — the `== null` guard means a position the user has since
    // picked via the dropdown is never clobbered by this effect re-running.
    useEffect(() => {
        if (stopCount <= 2) return;

        (dimensions || []).forEach((dim: any, index: number) => {
            if ((dim.pickupStopPosition === null || dim.pickupStopPosition === undefined) && dim.pickupStopId != null) {
                const pos = stopIdToPosition.get(dim.pickupStopId);

                if (pos != null) setValue(`dimensions.${index}.pickupStopPosition`, pos);
            }

            if ((dim.dropStopPosition === null || dim.dropStopPosition === undefined) && dim.dropStopId != null) {
                const pos = stopIdToPosition.get(dim.dropStopId);

                if (pos != null) setValue(`dimensions.${index}.dropStopPosition`, pos);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopIdToPosition, stopCount]);

    const scrollToNewDimension = useCallback((targetIndex: number) => {
        setTimeout(() => {
            const element = document.querySelector(`[data-dimension-index="${targetIndex}"]`);

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                const firstInput = element.querySelector('input[name="handlingUnitNo"]');

                if (firstInput) {
                    (firstInput as HTMLInputElement).focus();
                }
            }
        }, 400);
    }, []);

    // Force isStack=true (Do Not Stack ON) on any row whose height × 2 exceeds the bucket's stacking ceiling. Keeps form state aligned with the disabled toggle so saved data is consistent. Re-fires on measurement change because cm vs in changes the effective inches value.
    useEffect(() => {
        dimensions?.forEach((dim: any, idx: number) => {
            if (isStackingBlocked(primaryEquipmentType, dim?.height, weightMeasurement) && !dim?.isStack) {
                setValue(`dimensions.${idx}.isStack`, true);
            }
        });
    }, [primaryEquipmentType, weightMeasurement, JSON.stringify(dimensions?.map((d: any) => ({ h: d?.height, s: d?.isStack })))]);

    useEffect(() => {
        // The previous isInitialMount guard short-circuited on every tab remount (useRef resets), so equipment changes made on another tab never re-flowed into linearFootage. Always recompute when deps change.
        const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, primaryEquipmentType, false, MEASUREMENT.WEIGHT1.value, weightMeasurement)) || '';

        setValue('linearFootage', calLinearFootage);

        if (isSubmitted) {
            trigger('linearFootage');
        }
    }, [
        primaryEquipmentType,
        classification,
        weightMeasurement,
        JSON.stringify(
            dimensions?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                itemLength: item.itemLength,
                weight: item.weight,
                totalWeight: item.totalWeight,
                isStack: item.isStack,
            }))
        ),
    ]);

    useEffect(() => {
        if (dimensions && dimensions.length > 0) {
            dimensions.forEach((dimension: any, index: number) => {
                const weight = dimension.weight;
                const length = dimension.itemLength;
                const width = dimension.width;
                const height = dimension.height;

                if (weight && length && width && height) {
                    const freightData = getCalculatedFreightClass({
                        itemLength: length || 0,
                        width: width || 0,
                        height: height || 0,
                        weight: weight || 0,
                        lengthUnit: lengthMeasurement,
                        weightUnit: weightMeasurement,
                    });

                    const freightClass = freightData?.freightClass || 0;

                    setValue(`dimensions.${index}.freightClass`, freightClass);

                    if (isSubmitted) {
                        trigger(`dimensions.${index}.freightClass`);
                    }
                } else {
                    setValue(`dimensions.${index}.freightClass`, 0);
                }
            });
        }
    }, [
        watch('weightMeasurement'),
        watch('lengthMeasurement'),
        JSON.stringify(
            watch('dimensions')?.map((item: any) => ({
                weight: item.weight,
                length: item.length,
                width: item.width,
                height: item.height,
            }))
        ),
    ]);

    useEffect(() => {
        const currentTotalWeight = getValues('totalWeight');

        if (isInitialMount.current && currentTotalWeight && currentTotalWeight !== '') {
            isInitialMount.current = false;

            return;
        }

        isInitialMount.current = false;

        const totalDimensionsWeight = dimensions ? dimensions.reduce((sum: any, item: any) => +sum + (+item.totalWeight || 0), 0) : 0;
        const calTotalWeight = totalDimensionsWeight > 0 ? totalDimensionsWeight.toFixed(2) : '';

        setValue('totalWeight', calTotalWeight);

        if (isSubmitted) {
            trigger('totalWeight');
        }
    }, [
        JSON.stringify(
            dimensions?.map((item: any) => ({
                totalWeight: item.totalWeight,
            }))
        ),
    ]);

    const EDITABLE_SLUGS = ['in-bond', 'dangerous_goods'];

    const handleServiceChange = (e: any) => {
        let { value, checked, name } = e.target;
        value = parseInt(value);
        const currentServices = Array.isArray(selectedServices) ? selectedServices : [];

        if (checked) {
            if (name === 'in-bond') {
                handleAction('inBond')();
            } else if (name === 'dangerous_goods') {
                handleAction('dangerousGoods')();
            } else {
                setValue('serviceId', [...currentServices, value]);
            }
        } else {
            setValue(
                'serviceId',
                currentServices.filter((item: any) => item !== value)
            );

            if (name === 'in-bond') {
                setValue('inbond', false);
                setValue('inbondType', '');
                setValue('inbondName', '');
                setValue('inbondAddress', '');
                setValue('inbondContactMethod', '');
                setValue('inbondContactValue', '');
            } else if (name === 'dangerous_goods') {
                setValue('dangerousGoods', false);
                setValue('dangerGoodsType', '');
                setValue('unitedNationsNumber', '');
                setValue('packagingGroup', '');
                setValue('goodsClass', '');
                setValue('description', '');
                setValue('emergencyContactName', '');
                setValue('emergencyContactNumber', '');
                setValue('emergencyContactExt', '');
                setValue('emergencyEapnbr', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'in-bond') handleAction('inBond')();
        else if (service?.slug === 'dangerous_goods') handleAction('dangerousGoods')();
    };

    const handleInBond = (data: IInBondForm) => {
        const inBondId = findServiceTypeIdBySlug((serviceTypes as any).OTHER, 'in-bond');
        const currentServiceId = getValues('serviceId') || [];
        setValue('serviceId', currentServiceId.includes(inBondId) ? currentServiceId : [...currentServiceId, inBondId]);
        setValue('inbond', true);
        setValue('inbondType', data.inbondType);
        setValue('inbondName', data.inbondName);
        setValue('inbondAddress', data.inbondAddress);
        setValue('inbondContactMethod', data.inbondContactMethod);
        setValue('inbondContactValue', data.inbondContactValue);
        handleClose();
    };

    const handleDangerousGoods = (data: IDangerousGoodsForm) => {
        const dangerousGoodsId = findServiceTypeIdBySlug((serviceTypes as any).OTHER, 'dangerous_goods');
        const currentServiceId = getValues('serviceId') || [];
        setValue('serviceId', currentServiceId.includes(dangerousGoodsId) ? currentServiceId : [...currentServiceId, dangerousGoodsId]);
        setValue('dangerousGoods', true);
        setValue('dangerGoodsType', data.dangerGoodsType);
        setValue('unitedNationsNumber', data.unitedNationsNumber);
        setValue('packagingGroup', data.packagingGroup);
        setValue('goodsClass', data.goodsClass);
        setValue('description', data.description);
        setValue('emergencyContactName', data.emergencyContactName);
        setValue('emergencyContactNumber', data.emergencyContactNumber);
        setValue('emergencyContactExt', data.emergencyContactExt);
        setValue('emergencyEapnbr', data.emergencyEapnbr);
        handleClose();
    };

    const getOtherServiceList = (data: any) => (
        <div className="flex flex-wrap -m-1">
            {data.map((val: any) =>
                val.slug !== 'load_insurance' && (classification === QUOTE_CLASSIFICATION.FTL || (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null)) ? (
                    <div className="flex p-1 w-1/3" key={`service_${val.id}`}>
                        <CheckBox
                            id={`service_${val.id}`}
                            onChangeFunc={handleServiceChange}
                            name={val.slug}
                            value={val.id}
                            checked={selectedServices?.includes(val.id) || false}
                            parentClassName="mt-px"
                            disabled={isViewQuote}
                        />
                        <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-1.5 flex-1">
                            <label htmlFor={`service_${val.id}`} className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : 'cursor-pointer'}`}>
                                {val.name}
                            </label>
                            {selectedServices?.includes(val.id) && EDITABLE_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    </div>
                ) : null
            )}
        </div>
    );

    const handleOption = (onChange: any) => (e: any) => {
        onChange(e?.value);
    };

    const handleRemove = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length > 1) {
            removeOption(index);
            setOpenItems(new Set([dimensionsOptionFields.length - 2]));
        } else {
            setValue('dimensions', [initDimensionData]);
            setOpenItems(new Set([0]));
        }
    };

    const handleAdd = (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_DIMENSION_LIMIT) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const newOption: any = {
            id: null,
            handlingUnit: 1,
            handlingUnitNo: 0,
            weight: 0,
            itemLength: 0,
            width: 0,
            height: 0,
            totalWeight: 0,
            isStack: true,
            commodity: null,
            freightClass: 0,
            itemClassForDayross: 0,
        };

        const newIndex = dimensionsOptionFields.length;
        setOpenItems(new Set([newIndex]));
        appendOption(newOption);
        scrollToNewDimension(newIndex);
    };

    const handleDuplicate = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_DIMENSION_LIMIT) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const fieldValues = watch(`dimensions.${index}`);
        const newIndex = dimensionsOptionFields.length;
        setOpenItems(new Set([newIndex]));
        appendOption({ ...fieldValues, id: null });
        scrollToNewDimension(newIndex);
    };

    const toggleDetails = (index: any) => () => {
        const newOpenItems = new Set(openItems);
        setOpenItems((prev: any) => {
            if (!prev.has(index)) {
                newOpenItems.add(index);
            } else {
                newOpenItems.delete(index);
            }

            return newOpenItems;
        });
    };

    const handleInputChange = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // eventType  change
        // type number
        // name handlingUnitNo
        // value 1

        if (type === 'number') {
            value = value ? parseFloat(value) : eventType === 'blur' ? 0 : value;
        }

        let totalWeight = lineItemData.totalWeight;
        let weight = lineItemData.weight;
        let lineItemPerUnit = lineItemData.handlingUnitNo;

        if (name === 'weight') {
            if (lineItemPerUnit > 0) {
                totalWeight = value * lineItemPerUnit;
            } else {
                totalWeight = value;
            }
            weight = value;
        } else if (name === 'handlingUnitNo') {
            if (value > 0) {
                if (totalWeight > 0) {
                    weight = totalWeight / value;
                }
            } else {
                totalWeight = 0;
                weight = 0;
            }
        } else if (name === 'totalWeight') {
            if (lineItemPerUnit > 0) {
                weight = value / lineItemPerUnit;
            } else {
                weight = value;
            }
            totalWeight = value;
        }

        if (isNaN(totalWeight) || !isFinite(totalWeight)) {
            totalWeight = 0;
        }

        if (isNaN(weight) || !isFinite(weight)) {
            weight = 0;
        }

        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.totalWeight`, totalWeight);
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
            trigger(`dimensions.${index}.totalWeight`);
            trigger(`dimensions.${index}.weight`);
        }
    };

    const checkFreightClass = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;
        value = type === 'number' ? (value ? parseFloat(value) : eventType === 'blur' ? 0 : value) : value;
        let freightClass = lineItemData.freightClass;
        let itemClassForDayross = lineItemData.itemClassForDayross;
        let weight = lineItemData.weight;
        let itemLength = lineItemData.itemLength;
        let width = lineItemData.width;
        let height = lineItemData.height;

        if (weight && itemLength && width && height) {
            const freightData = getCalculatedFreightClass({
                itemLength: itemLength || 0,
                width: width || 0,
                height: height || 0,
                weight: weight || 0,
                lengthUnit: lengthMeasurement,
                weightUnit: weightMeasurement,
            });

            freightClass = freightData?.freightClass;
            itemClassForDayross = freightData?.itemClassForDayross;
        } else {
            freightClass = 0;
        }
        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.freightClass`, freightClass);
        setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
        }
    };

    const handleVisualizeUnit = (index: number) => (e: any) => {
        e.stopPropagation();
        setCurrentData({
            ...{ index: index, data: getValues(`dimensions.${index}`) },
        });
    };

    const handleVisualizeUnitClose = () => {
        setCurrentData(null);
    };

    const handleVisualizeUnitSubmit = (index: number, data: any) => {
        const fieldArray = ['itemLength', 'width', 'height', 'handlingUnit'];
        fieldArray.forEach((field) => {
            setValue(`dimensions.${index}.${field}`, data[field]);
        });
        handleVisualizeUnitClose();
    };

    const handleMeasurementChange = (onChange: any) => (event: any) => {
        const { value } = event.target;

        if (value === MEASUREMENT.WEIGHT1.value) {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH1.value);
        } else {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH2.value);
        }
        onChange(value);

        if (isSubmitted) {
            trigger();
        }
    };

    return (
        <>
            {/* Load Overview — Total Weight + Linear Footage + Other Additional Services in one card */}
            <div className="space-y-1.5 pt-4">
                <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Load_Overview')}</h6>
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                    {/* Total Weight + Linear Footage */}
                    <div className="flex gap-x-3">
                        <Controller
                            name="totalWeight"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputWrapClass={`${isViewQuote || isDimensionRequired ? 'transparent-disabled-input' : ''}`}
                                    label={t('Common.Total_Weight')}
                                    inputName="totalWeight"
                                    inputType="number"
                                    isAllowDecimal={true}
                                    maxLength={6}
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    labelClassName="mb-1.5 block"
                                    isIconError={!!error?.message}
                                    className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                    value={value}
                                    shouldFormatNumber={true}
                                    inputValueUnitType={weightMeasurement?.toLowerCase()}
                                    required
                                    onChangeFunc={onChange}
                                    errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                    isShowDisableClass={isDimensionRequired || isViewQuote}
                                    disabled={isViewQuote || isDimensionRequired}
                                />
                            )}
                        />
                        {classification === QUOTE_CLASSIFICATION.LTL || classification === QUOTE_CLASSIFICATION.VOLUME_LTL ? (
                            <Controller
                                name="linearFootage"
                                control={control}
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                    <InputText
                                        parentClassName="flex-1"
                                        inputWrapClass={`${isViewQuote || isDimensionRequired ? 'transparent-disabled-input' : ''}`}
                                        label={t('QuotingHub.QuotingDashboard.Linear_Footage_(Auto-calc)')}
                                        inputName="linearFootage"
                                        inputType="number"
                                        isIconError={!!error?.message}
                                        className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                        placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                        labelClassName="mb-1.5 block"
                                        value={value}
                                        required={classification !== QUOTE_CLASSIFICATION.FTL || isDimensionRequired}
                                        shouldFormatNumber={true}
                                        maxLength={2}
                                        onChangeFunc={onChange}
                                        inputValueUnitType="Ft."
                                        errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                        disabled={isViewQuote || isDimensionRequired}
                                        isShowDisableClass={isDimensionRequired || isViewQuote}
                                    />
                                )}
                            />
                        ) : null}
                    </div>

                    {/* Other Additional Services */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-x-1.5">
                            <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Other_Additional_Services')}</h6>
                            <div className="cursor-pointer" onClick={handleAction('service')}>
                                <TooltipCmp message={t('Common.Other_Additional_Services')}>
                                    <InfoCircle className="h-3.5 w-3.5 text-primary" />
                                </TooltipCmp>
                            </div>
                        </div>
                        <div className="rounded-lg border border-utilityGray200 bg-white shadow-sm p-3">
                            {!isVehicleTypeLoading && (serviceTypes as any).OTHER?.length ? (
                                getOtherServiceList((serviceTypes as any).OTHER)
                            ) : isVehicleTypeLoading ? (
                                <div className="flex flex-wrap -m-1">
                                    {Array.from({ length: 12 }).map((_, idx) => (
                                        <div className="flex p-1 w-1/3" key={`svc_loading_${idx}`}>
                                            <CheckBox id="svc_loading" onChangeFunc={() => {}} name="abc" value="xyz" checked={false} parentClassName="mt-0.5 custom-loading" />
                                            <div className="flex leading-4">
                                                <label className="md:ml-2 ml-1.5 text-sm leading-4 text-textSecondary font-medium custom-loading">Loading service</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <NotFoundUI
                                    iconClassName="!hidden"
                                    title={t('QuotingHub.QuotingDashboard.No_Services_Found')}
                                    desc={t('QuotingHub.QuotingDashboard.No_other_Services_found_desc')}
                                    containerClassName="!h-auto !my-5"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {classification === QUOTE_CLASSIFICATION.LTL || classification === QUOTE_CLASSIFICATION.VOLUME_LTL ? (
                <div>
                    <div className="flex justify-between items-center pb-3 pt-4 sticky top-0 bg-white z-[1]">
                        <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Dimensions')}</h6>
                        <div className={`flex items-center justify-center gap-1.5 `}>
                            <Controller
                                name="weightMeasurement"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <div className="flex items-center gap-3">
                                        <Radio
                                            onChangeFunc={handleMeasurementChange(onChange)}
                                            id="lbs"
                                            value={MEASUREMENT.WEIGHT1.value}
                                            checked={value === MEASUREMENT.WEIGHT1.value}
                                            labelName={t('Common.Imperial_(lbs)')}
                                            labelHtmlFor="lbs"
                                            radioLabelClassName="normal-case"
                                            disabled={isViewQuote}
                                            inputWrapClassName={`${isViewQuote ? 'pointer-events-none' : ''}`}
                                        />

                                        <Radio
                                            onChangeFunc={handleMeasurementChange(onChange)}
                                            checked={value === MEASUREMENT.WEIGHT2.value}
                                            value={MEASUREMENT.WEIGHT2.value}
                                            id="kgs"
                                            labelName={t('Common.Metric_(kgs)')}
                                            labelHtmlFor="kgs"
                                            radioLabelClassName={`normal-case ${isViewQuote ? 'pointer-events-none' : ''}`}
                                            disabled={isViewQuote}
                                            inputWrapClassName={`${isViewQuote ? 'pointer-events-none' : ''}`}
                                        />
                                        <hr className="w-px h-4 bg-borderPrimary" />
                                        {!isViewQuote && (
                                            <ButtonCmp type="submit" className="btn-link-outline-primary" size="none" onClick={handleAdd} icon={<Plus className="w-4 h-4" />}>
                                                {t('Common.Add_Line_Item')}
                                            </ButtonCmp>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                    <div className="space-y-3">
                        {dimensionsOptionFields.map((item: any, index) => (
                            <>
                                <div data-dimension-index={index} key={item.uuid} className="rounded-lg border border-gray100 bg-gray25 p-3">
                                    <>
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center gap-x-2.5 cursor-pointer pb-2 border-b border-gray100" onClick={toggleDetails(index)}>
                                                <h6 className="text-xs">
                                                    {t('Common.Line_Item')} {index + 1}
                                                </h6>
                                                <div className="flex gap-x-2">
                                                    {!isViewQuote && (
                                                        <TooltipCmp message="Duplicate">
                                                            <Copy03 className="w-4 h-4 hover:text-primary" onClick={handleDuplicate(index)} />
                                                        </TooltipCmp>
                                                    )}
                                                    <TooltipCmp message="Visualize Unit">
                                                        <Cube01 className="w-4 h-4 hover:text-primary" onClick={handleVisualizeUnit(index)} />
                                                    </TooltipCmp>

                                                    {!isViewQuote && (
                                                        <TooltipCmp message={t('Common.Delete')}>
                                                            <Trash01 className="w-4 h-4 hover:text-primary" onClick={handleRemove(index)} />
                                                        </TooltipCmp>
                                                    )}

                                                    <TooltipCmp message={openItems.has(index) ? t('QuotingHub.Collapse') : t('QuotingHub.Expand')}>
                                                        {openItems.has(index) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                    </TooltipCmp>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-x-3 gap-y-3 [&>*]:w-[calc(33.33%_-_8px)]">
                                                <Controller
                                                    name={`dimensions.${index}.handlingUnit`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <SelectBox
                                                            parentClassName="relative transparent-disabled-select"
                                                            label={t('Common.Handling_unit')}
                                                            labelClassName="form_label block mb-1.5"
                                                            isSearchable
                                                            name="handlingUnit"
                                                            required={true}
                                                            id="handlingUnit"
                                                            isDisabled={isViewQuote}
                                                            className="form_control"
                                                            classComp="mt-0 !bg-white"
                                                            options={handlingUnitOptions}
                                                            onChangeFunc={handleOption(onChange)}
                                                            value={handlingUnitOptions.find((handlingUnitOption: any) => handlingUnitOption.value === parseInt(value) || '')}
                                                            isError={!!error?.message}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.handlingUnitNo`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={t('Orders.NE_of_HU')}
                                                            labelClassName="block mb-1.5"
                                                            inputType="number"
                                                            inputName="handlingUnitNo"
                                                            maxLength={3}
                                                            isLoading={false}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={(e: any) => {
                                                                if (!e.target.value) {
                                                                    onChange(0);
                                                                }
                                                            }}
                                                            placeholder="0"
                                                            shouldFormatNumber={true}
                                                            value={value || ''}
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            disabled={isViewQuote}
                                                            required
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.weight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Unit_Weight')} ({getLabelByValue(weightMeasurement)?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="weight"
                                                            disabled={isViewQuote}
                                                            isLoading={false}
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            placeholder="0"
                                                            value={value || ''}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={getLabelByValue(weightMeasurement)?.toLowerCase()}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            isIconError={!!error?.message}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className={`grid transition-all duration-300 ease-in-out ${openItems.has(index) ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}>
                                            <div className="overflow-hidden flex flex-wrap gap-x-3 gap-y-3 [&>*]:w-[calc(33.33%_-_8px)]">
                                                <Controller
                                                    name={`dimensions.${index}.itemLength`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Length')} ({lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="itemLength"
                                                            disabled={isViewQuote}
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            isLoading={false}
                                                            placeholder="0"
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            value={value || ''}
                                                            onChangeFunc={onChange}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            isIconError={!!error?.message}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.width`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Width')} ({lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="width"
                                                            disabled={isViewQuote}
                                                            isLoading={false}
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            placeholder="0"
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            value={value || ''}
                                                            onChangeFunc={onChange}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            isIconError={!!error?.message}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.height`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Height')} ({lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            isLoading={false}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="height"
                                                            disabled={isViewQuote}
                                                            placeholder="0"
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            value={value || ''}
                                                            onChangeFunc={onChange}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            isIconError={!!error?.message}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.totalWeight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={`${t('Common.Total_Weight')} (${weightMeasurement?.toLowerCase()})`}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="totalWeight"
                                                            value={value || ''}
                                                            isLoading={false}
                                                            inputType="number"
                                                            isAllowDecimal={true}
                                                            maxLength={7}
                                                            placeholder="0"
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            disabled={isViewQuote}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                            required
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            isIconError={!!error?.message}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.freightClass`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            parentClassName="relative"
                                                            label={
                                                                <div className="flex">
                                                                    {t('QuotingHub.Common.Freight_Class')}
                                                                    <span className={`text-red-600 ms-[2px] leading-4 text-xs `}>*</span>
                                                                </div>
                                                            }
                                                            isLoading={false}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="freightClass"
                                                            inputType="number"
                                                            disabled={isViewQuote}
                                                            value={value || ''}
                                                            placeholder="0"
                                                            maxLength={3}
                                                            onChangeFunc={onChange}
                                                            shouldFormatNumber={true}
                                                            inputWrapClass={`${isViewQuote ? 'transparent-disabled-input' : ''}`}
                                                            isShowDisableClass={false}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`dimensions.${index}.commodity`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <SelectBox
                                                            parentClassName="relative"
                                                            placeholder={t('Common.Select_commodity')}
                                                            autoCompleteSelect={true}
                                                            name="commodity"
                                                            id={`commodity-${index}`}
                                                            className="form_control shadow"
                                                            label={t('Common.Commodity')}
                                                            labelClassName="form_label block "
                                                            isSearchable={true}
                                                            options={commodityOptions}
                                                            value={
                                                                value
                                                                    ? commodityOptions?.find((e: any) => e?.value == value) || {
                                                                          value: value,
                                                                          label: value,
                                                                      }
                                                                    : null
                                                            }
                                                            onChangeFunc={(e: any) => {
                                                                const selectedValue = e?.value || '';
                                                                onChange(selectedValue);
                                                            }}
                                                            isClearable={true}
                                                            onCreateOption={(e: any) => {
                                                                onChange(e);
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <div className="!w-full flex justify-between items-center">
                                                    <Controller
                                                        name={`dimensions.${index}.isStack`}
                                                        control={control}
                                                        render={({ field: { value, onChange } }) => {
                                                            const blocked = isStackingBlocked(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);
                                                            const blockedMessage = getStackingBlockedMessage(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);

                                                            const toggleEl = (
                                                                <Toggle
                                                                    isChecked={blocked || value}
                                                                    onChange={onChange}
                                                                    labelClassName={`flex items-center ${isViewQuote || blocked ? 'cursor-not-allowed' : ''}`}
                                                                    switchClassName={isViewQuote || blocked ? 'cursor-not-allowed' : 'cursor-pointer '}
                                                                    className={isViewQuote || blocked ? 'pointer-events-none' : ''}
                                                                    name="isStack"
                                                                    label={t('QuotingHub.Common.Do_not_stack')}
                                                                    labelPosition="right"
                                                                    labelTextClassName={`!text-xs ${isViewQuote || blocked ? 'pointer-events-none' : ''}`}
                                                                    id={`doNotStack-${index}`}
                                                                    isDisabled={isViewQuote || blocked}
                                                                />
                                                            );

                                                            return blocked ? (
                                                                <TooltipCmp message={blockedMessage}>
                                                                    <div className="inline-block">{toggleEl}</div>
                                                                </TooltipCmp>
                                                            ) : (
                                                                toggleEl
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Task 43 (user-reported) — per-item pick/drop routing, matching the Add Quote wizard's
                                            Dimensions step (LineItem.tsx). A SIBLING of (not nested inside) the collapsible
                                            "grid transition-all" wrapper above, so collapsing/expanding never touches this block —
                                            it renders in the exact same DOM position either way, right after Do Not Stack, same as
                                            before this feature ("stays the same in expanded view"). When the card is collapsed the
                                            wrapper above visually shrinks to 0 height, so this ends up directly under the collapsed
                                            row — satisfying "show even collapsed" without moving anything relative to the expanded
                                            layout. Gated on stopCount > 2 so classic 2-stop quotes render identically to before this
                                            feature. No <Controller> — validated manually on submit (QuoteDetailModalNew.tsx), same as
                                            the create flow. */}
                                        {stopCount > 2 && (
                                            <div className="border-t border-gray100 pt-3 mt-3 flex gap-x-3">
                                                <SelectBox
                                                    parentClassName="flex-1"
                                                    label={t('MyQuote.Picks_Up_At')}
                                                    labelClassName="form_label block mb-1.5"
                                                    name="pickupStopPosition"
                                                    id={`pickupStopPosition-${index}`}
                                                    className="form_control"
                                                    isDisabled={isViewQuote}
                                                    options={pickupOptions}
                                                    value={pickupOptions.find((option) => option.value === (watch(`dimensions.${index}.pickupStopPosition`) ?? 1)) ?? pickupOptions[0]}
                                                    onChangeFunc={(option: any) => setValue(`dimensions.${index}.pickupStopPosition`, option.value)}
                                                />
                                                <SelectBox
                                                    parentClassName="flex-1"
                                                    label={t('MyQuote.Delivers_To')}
                                                    labelClassName="form_label block mb-1.5"
                                                    name="dropStopPosition"
                                                    id={`dropStopPosition-${index}`}
                                                    className="form_control"
                                                    isDisabled={isViewQuote}
                                                    options={dropOptions}
                                                    value={
                                                        dropOptions.find((option) => option.value === (watch(`dimensions.${index}.dropStopPosition`) ?? stopCount)) ??
                                                        dropOptions[dropOptions.length - 1]
                                                    }
                                                    onChangeFunc={(option: any) => setValue(`dimensions.${index}.dropStopPosition`, option.value)}
                                                />
                                            </div>
                                        )}
                                    </>
                                </div>
                            </>
                        ))}
                    </div>
                </div>
            ) : null}

            {action.service && (
                <AdditionalServiceModal
                    handleClose={handleAction('service', false)}
                    services={((serviceTypes as any).OTHER || []).filter(
                        (val: any) => val.slug !== 'load_insurance' && (classification === QUOTE_CLASSIFICATION.FTL || (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null))
                    )}
                    title={t('Common.Other_Additional_Services')}
                />
            )}

            {action.inBond && (
                <InBondModal
                    handleClose={handleClose}
                    handleInBond={handleInBond}
                    defaultFormValues={
                        {
                            inbondType: watch('inbondType') || '',
                            inbondName: watch('inbondName') || '',
                            inbondAddress: watch('inbondAddress') || '',
                            inbondContactValue: watch('inbondContactValue') || '',
                            inbondContactMethod: watch('inbondContactMethod') || 'email-address',
                            countryCode: (watch('inbondContactValue') ? parsePhoneNumberFromString(watch('inbondContactValue'))?.country : undefined) || 'CA',
                        } as IInBondForm
                    }
                />
            )}

            {action.dangerousGoods && (
                <DangerousGoodsModal
                    handleClose={handleClose}
                    handleDangerousGoods={handleDangerousGoods}
                    defaultFormValues={
                        {
                            dangerGoodsType: watch('dangerGoodsType') || '',
                            unitedNationsNumber: watch('unitedNationsNumber') || '',
                            packagingGroup: watch('packagingGroup') || '',
                            goodsClass: watch('goodsClass') || '',
                            description: watch('description') || '',
                            emergencyContactName: watch('emergencyContactName') || '',
                            emergencyContactNumber: watch('emergencyContactNumber') || '',
                            emergencyContactExt: watch('emergencyContactExt') || '',
                            emergencyEapnbr: watch('emergencyEapnbr') || '1-888-CAN-UTEC',
                            emergencyContactCountryCode: (watch('emergencyContactNumber') ? parsePhoneNumberFromString(watch('emergencyContactNumber'))?.country : undefined) || 'CA',
                        } as IDangerousGoodsForm
                    }
                />
            )}

            {currentData && (
                <VisualizeUnit
                    currentData={currentData}
                    handleClose={handleVisualizeUnitClose}
                    weightMeasurement={weightMeasurement}
                    lengthMeasurement={lengthMeasurement}
                    parentFreightClass={getValues(`dimensions.${currentData.index}.freightClass`)}
                    unitArr={handlingUnitOptions}
                    isFormDataHaveKey={true}
                    handleVisualizeUnitSubmit={handleVisualizeUnitSubmit}
                    primaryEquipmentType={primaryEquipmentType ?? ''}
                    primaryEquipmentTypeFullName={watch('primaryEquipmentTypeFullName') ?? ''}
                />
            )}
        </>
    );
};

export default DimensionsTab;
